/**
 * SharePoint file operation tools via Microsoft Graph
 */

import { z } from "zod";
import type { GraphApi } from "../api/graph-api.js";
import { logger } from "../utils/logger.js";

// =============================================================================
// Tool Definitions
// =============================================================================

export const listSharePointFilesTool = {
  name: "list_sharepoint_files",
  description:
    "List files in a SharePoint document library. Use list_sharepoint_lists first to find document libraries, then use list_sharepoint_lists with the site ID to get drive IDs via the library's driveType.",
  inputSchema: {
    type: "object" as const,
    properties: {
      siteId: {
        type: "string",
        description: "The SharePoint site ID",
      },
      driveId: {
        type: "string",
        description: "The drive (document library) ID. Use list_sharepoint_lists or get the drive from the site.",
      },
      folderPath: {
        type: "string",
        description: "Optional folder path within the drive (e.g., 'Reports/2024')",
      },
    },
    required: ["siteId", "driveId"],
  },
};

export const uploadSharePointFileTool = {
  name: "upload_sharepoint_file",
  description:
    "Upload a file to a SharePoint document library. Simple upload supports files up to 4MB. Provide the file content as a base64-encoded string.",
  inputSchema: {
    type: "object" as const,
    properties: {
      siteId: {
        type: "string",
        description: "The SharePoint site ID",
      },
      driveId: {
        type: "string",
        description: "The drive (document library) ID",
      },
      filePath: {
        type: "string",
        description: "The destination file path in the drive (e.g., 'Reports/monthly-report.xlsx')",
      },
      content: {
        type: "string",
        description: "The file content as a base64-encoded string",
      },
      contentType: {
        type: "string",
        description: "The MIME type (e.g., 'text/plain', 'application/pdf'). Default: 'application/octet-stream'",
      },
    },
    required: ["siteId", "driveId", "filePath", "content"],
  },
};

export const getSharePointFileContentTool = {
  name: "get_sharepoint_file_content",
  description:
    "Download a file's content from a SharePoint document library. Returns base64-encoded content for text files under 1MB, metadata-only for larger or binary files.",
  inputSchema: {
    type: "object" as const,
    properties: {
      siteId: {
        type: "string",
        description: "The SharePoint site ID",
      },
      driveId: {
        type: "string",
        description: "The drive (document library) ID",
      },
      itemId: {
        type: "string",
        description: "The file item ID",
      },
    },
    required: ["siteId", "driveId", "itemId"],
  },
};

// =============================================================================
// Input Schemas
// =============================================================================

export const listSharePointFilesInputSchema = z.object({
  siteId: z.string().min(1),
  driveId: z.string().min(1),
  folderPath: z.string().optional(),
});

export const uploadSharePointFileInputSchema = z.object({
  siteId: z.string().min(1),
  driveId: z.string().min(1),
  filePath: z.string().min(1),
  content: z.string().min(1),
  contentType: z.string().optional(),
});

export const getSharePointFileContentInputSchema = z.object({
  siteId: z.string().min(1),
  driveId: z.string().min(1),
  itemId: z.string().min(1),
});

// =============================================================================
// Tool Handlers
// =============================================================================

const GRAPH_NOT_AVAILABLE = "Graph API not configured. SharePoint file operations require Microsoft Graph access.";

const TEXT_MIME_TYPES = new Set([
  "text/plain",
  "text/csv",
  "text/html",
  "text/xml",
  "application/json",
  "application/xml",
  "text/markdown",
  "text/tab-separated-values",
]);

const MAX_TEXT_SIZE = 1024 * 1024; // 1MB

export async function handleListSharePointFiles(
  graphApi: GraphApi | null,
  input: unknown
): Promise<string> {
  if (!graphApi) return GRAPH_NOT_AVAILABLE;

  const parsed = listSharePointFilesInputSchema.parse(input);
  logger.info({ siteId: parsed.siteId, driveId: parsed.driveId }, "Executing list_sharepoint_files");

  const items = await graphApi.listSiteFiles(parsed.siteId, parsed.driveId, parsed.folderPath);

  if (items.length === 0) {
    return `No files found${parsed.folderPath ? ` in folder "${parsed.folderPath}"` : " in root"}.`;
  }

  const lines = [`Files (${items.length}):`];
  for (const item of items) {
    if (item.folder) {
      lines.push(`  📁 ${item.name} (${item.folder.childCount} items)`);
    } else {
      const mime = item.file?.mimeType || "unknown";
      lines.push(`  📄 ${item.name}`);
      lines.push(`    ID: ${item.id}`);
      lines.push(`    Type: ${mime}`);
      if (item.webUrl) lines.push(`    URL: ${item.webUrl}`);
    }
  }

  return lines.join("\n");
}

export async function handleUploadSharePointFile(
  graphApi: GraphApi | null,
  input: unknown
): Promise<string> {
  if (!graphApi) return GRAPH_NOT_AVAILABLE;

  const parsed = uploadSharePointFileInputSchema.parse(input);
  logger.info({ siteId: parsed.siteId, driveId: parsed.driveId, filePath: parsed.filePath }, "Executing upload_sharepoint_file");

  // Estimate decoded size before allocating buffer (~75% of base64 length)
  const estimatedSize = Math.ceil(parsed.content.length * 0.75);
  if (estimatedSize > 4 * 1024 * 1024) {
    return "File too large for simple upload. Maximum size is 4MB. For larger files, use the Power Automate SharePoint connector's \"Create file\" action.";
  }

  const buffer = Buffer.from(parsed.content, "base64");

  if (buffer.length > 4 * 1024 * 1024) {
    return "File too large for simple upload. Maximum size is 4MB. For larger files, use the Power Automate SharePoint connector's \"Create file\" action.";
  }

  const item = await graphApi.uploadSiteFile(
    parsed.siteId,
    parsed.driveId,
    parsed.filePath,
    buffer,
    parsed.contentType || "application/octet-stream"
  );

  const lines = [
    `Uploaded file successfully:`,
    `  Name: ${item.name}`,
    `  ID: ${item.id}`,
  ];
  if (item.webUrl) lines.push(`  URL: ${item.webUrl}`);

  return lines.join("\n");
}

export async function handleGetSharePointFileContent(
  graphApi: GraphApi | null,
  input: unknown
): Promise<string> {
  if (!graphApi) return GRAPH_NOT_AVAILABLE;

  const parsed = getSharePointFileContentInputSchema.parse(input);
  logger.info({ siteId: parsed.siteId, driveId: parsed.driveId, itemId: parsed.itemId }, "Executing get_sharepoint_file_content");

  const { data, contentType } = await graphApi.getSiteFileContent(parsed.siteId, parsed.driveId, parsed.itemId);

  const isText = TEXT_MIME_TYPES.has(contentType) || contentType.startsWith("text/");
  const size = data.length;

  if (isText && size <= MAX_TEXT_SIZE) {
    const text = data.toString("utf-8");
    return `File content (${contentType}, ${size} bytes):\n\n${text}`;
  }

  if (isText && size > MAX_TEXT_SIZE) {
    return `File is a text file (${contentType}) but too large to display (${(size / 1024).toFixed(1)} KB). Download it from SharePoint directly.`;
  }

  // Binary file — return base64 for small files, metadata only for large
  if (size <= MAX_TEXT_SIZE) {
    return `File content (${contentType}, ${size} bytes, base64-encoded):\n\n${data.toString("base64")}`;
  }

  return `Binary file (${contentType}, ${(size / 1024).toFixed(1)} KB). Too large to return inline. Download from SharePoint directly.`;
}
